import React, { useEffect, useCallback, useMemo } from 'react';
import { useUserConfig, useUserConfigDispatch } from '@/context/UserConfigContext';
import { OvernightConfigForm } from '@/components/config_forms/overnight/OvernightConfigForm';
import { toast } from 'sonner';
import { useOptionalFieldValidation } from '@/components/validation';
import { useDefaultExtendedTiers } from '@/hooks/useDefaultExtendedTiers';
import type { OvernightConfig as TOvernightConfig, UserConfigurationState } from '@/types';
import { cn } from '@/lib/utils';

interface UserOvernightConfigProps {
  noCard?: boolean;
  roles?: UserConfigurationState['roles'];
  rateMultipliers?: UserConfigurationState['rateMultipliers'];
}

export const UserOvernightConfig: React.FC<UserOvernightConfigProps> = ({
  noCard,
  roles: propRoles,
  rateMultipliers: propRateMultipliers,
}) => {
  const { state } = useUserConfig();
  const { updateBillingConfig } = useUserConfigDispatch();
  const defaultExtendedTiers = useDefaultExtendedTiers('overnight');

  const overnightConfig = useMemo(() => state.overnightConfig || {
    applyOvernightPremium: false,
    startTime: '00:00',
    endTime: '06:00',
    overnightMethod: null,
    thresholdEnabled: false,
    thresholdType: 'hours',
    percentageThreshold: 50,
    hoursThreshold: 4,
    premiumType: null,
    premiumValue: null,
    flatRateAmount: null,
    timeCreditMinutes: null,
    premiumBasis: 'base_rate',
    applyOnHalfDays: true,
    applicableRoleIds: null,
  }, [state.overnightConfig]);

  const hasThresholdMultipliers = (state.rateMultipliers || []).some(m =>
    m.code !== 'STRAIGHT_TIME' &&
    (m.defaultPerShiftThresholdHours != null || m.defaultPerWeekThresholdHours != null)
  );

  useEffect(() => {
    if (overnightConfig.applyOvernightPremium && overnightConfig.overnightMethod === 'offset_overtime' && !hasThresholdMultipliers) {
      onConfigChange({ premiumType: null });
      toast.info('Overnight premium method reset: "Offset Overtime" is unavailable without defined overtime thresholds.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasThresholdMultipliers, overnightConfig.applyOvernightPremium, overnightConfig.premiumType]);

  const onConfigChange = useCallback((updates: Partial<TOvernightConfig>) => {
    const newConfig = { ...overnightConfig, ...updates };

    if (updates.thresholdEnabled && !newConfig.thresholdType) {
      newConfig.thresholdType = 'hours';
    }
    if (updates.thresholdType === 'percentage') {
      newConfig.hoursThreshold = null;
    } else if (updates.thresholdType === 'hours') {
      newConfig.percentageThreshold = null;
    }
    if (updates.thresholdEnabled === false) {
      newConfig.thresholdType = null;
      newConfig.percentageThreshold = null;
      newConfig.hoursThreshold = null;
    }
    if (updates.overnightMethod === 'hourly_premium' && !newConfig.premiumType) {
      newConfig.premiumType = 'fixed';
    }
    updateBillingConfig({ overnightConfig: newConfig });
  }, [updateBillingConfig, overnightConfig]);

  // Field validations via ValidationProvider schemas
  const startTime = useOptionalFieldValidation('overnightConfig.startTime');
  const endTime = useOptionalFieldValidation('overnightConfig.endTime');
  const premiumVal = useOptionalFieldValidation('overnightConfig.premiumValue');
  const flatRate = useOptionalFieldValidation('overnightConfig.flatRateAmount');
  const timeCredit = useOptionalFieldValidation('overnightConfig.timeCreditMinutes');
  const pctThreshold = useOptionalFieldValidation('overnightConfig.percentageThreshold');
  const hoursThreshold = useOptionalFieldValidation('overnightConfig.hoursThreshold');

  const buildInputProps = (
    fieldKey: string,
    value: unknown,
    onChange: (v: unknown) => void,
    validation: ReturnType<typeof useOptionalFieldValidation>,
  ): Record<string, unknown> => ({
    'data-validation-key': fieldKey,
    value: value ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value ? parseFloat(e.target.value) : null;
      onChange(v);
    },
    onBlur: validation.onBlur,
    className: cn(validation.hasError && "border-destructive focus-visible:ring-destructive focus-visible:border-destructive"),
  });

  const startTimeProps = {
    ...buildInputProps('overnightConfig.startTime', overnightConfig.startTime, (v) => onConfigChange({ startTime: v as string | null }), startTime),
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => onConfigChange({ startTime: e.target.value || null }),
  };

  const endTimeProps = {
    ...buildInputProps('overnightConfig.endTime', overnightConfig.endTime, (v) => onConfigChange({ endTime: v as string | null }), endTime),
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => onConfigChange({ endTime: e.target.value || null }),
  };

  return (
    <OvernightConfigForm
      title="Overnight Premium"
      description="Define your standard rules and premiums for overnight work."
      config={overnightConfig}
      context="user"
      onConfigChange={onConfigChange}
      noCard={noCard}
      hideToggle={true}
      startTimeProps={startTimeProps}
      startTimeError={startTime.errorElement}
      endTimeProps={endTimeProps}
      endTimeError={endTime.errorElement}
      premiumValueProps={buildInputProps('overnightConfig.premiumValue', overnightConfig.premiumValue, (v) => onConfigChange({ premiumValue: v as number | null }), premiumVal)}
      premiumValueError={premiumVal.errorElement}
      flatRateAmountProps={buildInputProps('overnightConfig.flatRateAmount', overnightConfig.flatRateAmount, (v) => onConfigChange({ flatRateAmount: v as number | null }), flatRate)}
      flatRateAmountError={flatRate.errorElement}
      timeCreditMinutesProps={buildInputProps('overnightConfig.timeCreditMinutes', overnightConfig.timeCreditMinutes, (v) => onConfigChange({ timeCreditMinutes: v as number | null }), timeCredit)}
      timeCreditMinutesError={timeCredit.errorElement}
      thresholdEnabled={overnightConfig.thresholdEnabled}
      onThresholdEnabledChange={(checked) => onConfigChange({ thresholdEnabled: checked })}
      thresholdType={overnightConfig.thresholdType}
      onThresholdTypeChange={(type) => onConfigChange({ thresholdType: type })}
      percentageThresholdProps={buildInputProps('overnightConfig.percentageThreshold', overnightConfig.percentageThreshold ?? null, (v) => onConfigChange({ percentageThreshold: v as number | null }), pctThreshold)}
      percentageThresholdError={pctThreshold.errorElement}
      hoursThresholdProps={buildInputProps('overnightConfig.hoursThreshold', overnightConfig.hoursThreshold ?? null, (v) => onConfigChange({ hoursThreshold: v as number | null }), hoursThreshold)}
      hoursThresholdError={hoursThreshold.errorElement}
      hasThresholdMultipliers={hasThresholdMultipliers}
      rateMultipliers={propRateMultipliers ?? state.rateMultipliers ?? []}
      roles={propRoles ?? state.roles ?? []}
      minimumBillableHours={state.billingStructureConfig?.minimumBillableHours}
      extendedOvertimeEnabled={overnightConfig.extendedOvertimeEnabled ?? false}
      onExtendedOvertimeEnabledChange={(v) => onConfigChange({ extendedOvertimeEnabled: v })}
      extendedOvertimeTiers={overnightConfig.extendedOvertimeTiers ?? []}
      onExtendedOvertimeTiersChange={(tiers) => onConfigChange({ extendedOvertimeTiers: tiers })}
      defaultExtendedTiers={defaultExtendedTiers}
    />
  );
};
